// ============================================
// WEBSOCKET CLIENT — INTEGRATED
// React hooks, connection state, message buffering,
// heartbeat/ping, subscription management, reconnection
// ============================================

import { useState, useEffect, useCallback } from 'react';

const WS_URL = import.meta.env.VITE_WS_URL || 'wss://luco1-cloudflare.your-subdomain.workers.dev';

// ---- Types ----

export interface WSMessage {
  type: string;
  data?: any;
  timestamp?: number;
}

export interface WSSubscription {
  entities: string[];
  playerId?: number;
}

export interface ConnectionState {
  status: 'connecting' | 'connected' | 'disconnected' | 'reconnecting';
  lastConnectedAt: number | null;
  reconnectAttempts: number;
  latencyMs: number | null;
}

// ---- WebSocket Client ----

class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private stateListeners: Set<(state: ConnectionState) => void> = new Set();
  private messageQueue: WSMessage[] = [];
  private subscriptions: WSSubscription[] = [];
  private state: ConnectionState = {
    status: 'disconnected',
    lastConnectedAt: null,
    reconnectAttempts: 0,
    latencyMs: null,
  };
  private heartbeatInterval = 30000; // 30s
  private heartbeatTimeout = 60000; // 60s
  private maxReconnectAttempts = 10;
  private reconnectBaseDelay = 1000;
  private reconnectMaxDelay = 30000;
  private lastPingTime = 0;
  private shouldReconnect = true;

  // ---- Connection Management ----

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.shouldReconnect = true;
    this.updateState({ status: 'connecting' });

    try {
      this.ws = new WebSocket(WS_URL);

      this.ws.onopen = () => {
        console.log('[WS] Connected');
        this.updateState({
          status: 'connected',
          lastConnectedAt: Date.now(),
          reconnectAttempts: 0,
        });

        // Flush queued messages
        this.flushQueue();

        // Restore subscriptions
        this.restoreSubscriptions();

        // Start heartbeat
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WSMessage = JSON.parse(event.data);

          // Handle heartbeat response
          if (message.type === 'pong') {
            const latency = Date.now() - this.lastPingTime;
            this.updateState({ latencyMs: latency });
            return;
          }

          // Handle state sync
          if (message.type === 'state_sync') {
            this.emit('state_sync', message.data);
            return;
          }

          this.emit(message.type, message.data);
        } catch (err) {
          console.error('[WS] Parse error:', err);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[WS] Disconnected', event.code, event.reason);
        this.stopHeartbeat();
        this.updateState({ status: 'disconnected' });

        if (this.shouldReconnect && this.state.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (err) => {
        console.error('[WS] Error:', err);
        this.updateState({ status: 'disconnected' });
      };
    } catch (err) {
      console.error('[WS] Connection failed:', err);
      this.updateState({ status: 'disconnected' });
      this.scheduleReconnect();
    }
  }

  disconnect() {
    this.shouldReconnect = false;
    this.stopHeartbeat();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.ws?.close();
    this.ws = null;
    this.updateState({ status: 'disconnected' });
  }

  // ---- Heartbeat ----

  private startHeartbeat() {
    this.heartbeatTimer = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.lastPingTime = Date.now();
        this.send({ type: 'ping', timestamp: Date.now() });

        // Check for timeout
        setTimeout(() => {
          if (Date.now() - this.lastPingTime > this.heartbeatTimeout && this.state.status === 'connected') {
            console.warn('[WS] Heartbeat timeout, reconnecting');
            this.ws?.close();
          }
        }, this.heartbeatTimeout);
      }
    }, this.heartbeatInterval);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // ---- Reconnection ----

  private scheduleReconnect() {
    const attempt = this.state.reconnectAttempts + 1;
    const delay = Math.min(
      this.reconnectBaseDelay * Math.pow(2, attempt - 1),
      this.reconnectMaxDelay
    );

    this.updateState({
      status: 'reconnecting',
      reconnectAttempts: attempt,
    });

    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${attempt})`);

    this.reconnectTimer = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  // ---- Message Queue ----

  private flushQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        this.send(message);
      }
    }
  }

  send(message: WSMessage | any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Queue message for when connection resumes
      this.messageQueue.push(message as WSMessage);
    }
  }

  // ---- Subscriptions ----

  subscribe(subscription: WSSubscription) {
    this.subscriptions.push(subscription);
    this.send({
      type: 'subscribe',
      entities: subscription.entities,
      playerId: subscription.playerId,
    });
  }

  unsubscribe(entities: string[]) {
    this.subscriptions = this.subscriptions.filter(
      sub => !sub.entities.some(e => entities.includes(e))
    );
    this.send({ type: 'unsubscribe', entities });
  }

  private restoreSubscriptions() {
    for (const sub of this.subscriptions) {
      this.send({
        type: 'subscribe',
        entities: sub.entities,
        playerId: sub.playerId,
      });
    }
  }

  // ---- Event System ----

  on(event: string, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  private emit(event: string, data: any) {
    this.listeners.get(event)?.forEach(cb => {
      try {
        cb(data);
      } catch (err) {
        console.error('[WS] Listener error:', err);
      }
    });
  }

  // ---- State Management ----

  private updateState(partial: Partial<ConnectionState>) {
    this.state = { ...this.state, ...partial };
    this.stateListeners.forEach(cb => {
      try {
        cb(this.state);
      } catch (err) {
        console.error('[WS] State listener error:', err);
      }
    });
  }

  onStateChange(callback: (state: ConnectionState) => void) {
    this.stateListeners.add(callback);
    callback(this.state); // Initial state

    return () => {
      this.stateListeners.delete(callback);
    };
  }

  getState(): ConnectionState {
    return { ...this.state };
  }

  // ---- Helpers ----

  isConnected(): boolean {
    return this.state.status === 'connected';
  }

  getQueueLength(): number {
    return this.messageQueue.length;
  }
}

// ---- Singleton Export ----

export const ws = new WebSocketClient();

// ============================================
// REACT HOOKS
// ============================================

export function useWebSocket() {
  const [state, setState] = useState<ConnectionState>(ws.getState());

  useEffect(() => {
    const unsubscribe = ws.onStateChange(setState);
    return unsubscribe;
  }, []);

  const connect = useCallback(() => ws.connect(), []);
  const disconnect = useCallback(() => ws.disconnect(), []);
  const send = useCallback((message: WSMessage) => ws.send(message), []);
  const subscribe = useCallback((sub: WSSubscription) => ws.subscribe(sub), []);
  const unsubscribe = useCallback((entities: string[]) => ws.unsubscribe(entities), []);

  return {
    state,
    isConnected: state.status === 'connected',
    connect,
    disconnect,
    send,
    subscribe,
    unsubscribe,
  };
}

export function useWebSocketEvent(event: string) {
  const [data, setData] = useState<any>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(0);

  useEffect(() => {
    const unsubscribe = ws.on(event, (newData: any) => {
      setData(newData);
      setLastUpdate(Date.now());
    });
    return unsubscribe;
  }, [event]);

  return { data, lastUpdate };
}

export function useSimulationState() {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [npcCounts, setNpcCounts] = useState<{ facilities: Record<number, number>; projects: Record<number, number> }>({
    facilities: {},
    projects: {},
  });
  const [resources, setResources] = useState<Record<number, any>>({});
  const [impact, setImpact] = useState<Record<number, any>>({});
  const [leaderboard, setLeaderboard] = useState<any>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const unsubTick = ws.on('tick', (data: any) => {
      setTick(data.tick || 0);
      if (data.vehicles) setVehicles(data.vehicles);
    });

    const unsubVehicle = ws.on('vehicle', (data: any) => {
      setVehicles(prev => {
        const index = prev.findIndex(v => v.id === data.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = { ...updated[index], ...data };
          return updated;
        }
        return [...prev, data];
      });
    });

    const unsubNPC = ws.on('npc', (data: any) => {
      setNpcCounts(prev => {
        if (data.locationId < 100) {
          return {
            ...prev,
            facilities: { ...prev.facilities, [data.locationId]: data.workersCount },
          };
        }
        return {
          ...prev,
          projects: { ...prev.projects, [data.locationId]: data.workersCount },
        };
      });
    });

    const unsubResource = ws.on('resource', (data: any) => {
      setResources(prev => ({ ...prev, [data.facilityId]: data }));
    });

    const unsubImpact = ws.on('impact', (data: any) => {
      setImpact(prev => ({ ...prev, [data.facilityId]: data }));
    });

    const unsubLeaderboard = ws.on('leaderboard', (data: any) => {
      setLeaderboard(data);
    });

    const unsubStateSync = ws.on('state_sync', (data: any) => {
      if (data.vehicles) setVehicles(data.vehicles);
      if (data.facilityNPCs) {
        setNpcCounts(prev => ({
          facilities: data.facilityNPCs,
          projects: prev.projects,
        }));
      }
      if (data.projectNPCs) {
        setNpcCounts(prev => ({
          facilities: prev.facilities,
          projects: data.projectNPCs,
        }));
      }
    });

    return () => {
      unsubTick();
      unsubVehicle();
      unsubNPC();
      unsubResource();
      unsubImpact();
      unsubLeaderboard();
      unsubStateSync();
    };
  }, []);

  return {
    vehicles,
    npcCounts,
    resources,
    impact,
    leaderboard,
    tick,
  };
}

// ---- Connection Status Component Helper ----

export function getConnectionStatusColor(status: ConnectionState['status']): string {
  switch (status) {
    case 'connected': return 'text-green-400';
    case 'connecting': return 'text-yellow-400';
    case 'reconnecting': return 'text-orange-400';
    case 'disconnected': return 'text-red-400';
    default: return 'text-gray-400';
  }
}

export function getConnectionStatusLabel(status: ConnectionState['status']): string {
  switch (status) {
    case 'connected': return '🟢 Live';
    case 'connecting': return '🟡 Connecting...';
    case 'reconnecting': return '🟠 Reconnecting...';
    case 'disconnected': return '🔴 Offline';
    default: return '⚪ Unknown';
  }
}